/**
 * Profanity filter for lead form enquiry field.
 * Checks text against a list of profane words.
 * Returns true if profanity is detected.
 */

const PROFANITY_WORDS = [
  // General
  "ass",
  "asshole",
  "bastard",
  "bitch",
  "bloody",
  "bollocks",
  "bugger",
  "bullshit",
  "crap",
  "damn",
  "dick",
  "dickhead",
  "douche",
  "dumbass",
  "freaking",
  "fricking",
  "hell",
  "idiot",
  "jerk",
  "loser",
  "moron",
  "piss",
  "prick",
  "shit",
  "shitty",
  "stupid",
  "suck",
  "twat",
  "wanker",
  "wtf",

  // Strong
  "bitch",
  "cock",
  "cunt",
  "fag",
  "faggot",
  "fuck",
  "fucker",
  "fucking",
  "motherfucker",
  "nigga",
  "nigger",
  "pussy",
  "slut",
  "whore",

  // Sexual / Explicit
  "blowjob",
  "boner",
  "cum",
  "cumming",
  "dildo",
  "handjob",
  "masturbate",
  "orgasm",
  "penis",
  "porn",
  "sex",
  "vagina",
  "xxx",

  // Common bypass variations
  "fck",
  "fk",
  "fucc",
  "fuccc",
  "fuk",
  "fux",
  "sh1t",
  "b1tch",
  "a55",
  "azz",
  "biatch",
  "mf",
  "mfer",

  // Toxic insults
  "retard",
  "retarded",
  "clown",
  "scumbag",
  "trash",
  "garbage",
  "noob",

  // Hate / abusive
  "racist",
  "pedo",
  "pedophile",
  "terrorist",
];

/** Checks if text contains any profane words (case-insensitive, word boundary match) */
export const containsProfanity = (text: string): boolean => {
  if (!text) return false;
  const lowerText = text.toLowerCase();
  return PROFANITY_WORDS.some((word) => {
    const regex = new RegExp(`\\b${word}\\b`, "i");
    return regex.test(lowerText);
  });
};

/** Removes all profane words from text (case-insensitive, word boundary match) */
export const removeProfanity = (text: string): string => {
  if (!text) return text;
  let cleaned = text;
  PROFANITY_WORDS.forEach((word) => {
    const regex = new RegExp(`\\b${word}\\b`, "gi");
    cleaned = cleaned.replace(regex, "");
  });
  // Clean up extra spaces left after removal
  return cleaned.replace(/\s{2,}/g, " ").trim();
};

export interface ProfanityMatch {
  word: string;
  start: number;
  end: number;
}

/** Returns all profane word matches with their positions in the text */
export const getProfanityMatches = (text: string): ProfanityMatch[] => {
  if (!text) return [];
  const matches: ProfanityMatch[] = [];
  PROFANITY_WORDS.forEach((word) => {
    const regex = new RegExp(`\\b${word}\\b`, "gi");
    let match;
    while ((match = regex.exec(text)) !== null) {
      matches.push({
        word: match[0],
        start: match.index,
        end: match.index + match[0].length,
      });
    }
  });
  // Sort by position and deduplicate overlapping matches
  matches.sort((a, b) => a.start - b.start);
  return matches;
};

/** Generates highlighted HTML text with profane words wrapped in mark tags */
export const getHighlightedText = (text: string): string => {
  if (!text) return "";
  const matches = getProfanityMatches(text);
  if (matches.length === 0) return escapeHtml(text);

  let result = "";
  let lastIndex = 0;
  matches.forEach(({ start, end }) => {
    if (start < lastIndex) return; // skip overlapping
    result += escapeHtml(text.slice(lastIndex, start));
    result += `<mark class="bg-negative-100 text-negative-default rounded-[2px]">${escapeHtml(text.slice(start, end))}</mark>`;
    lastIndex = end;
  });
  result += escapeHtml(text.slice(lastIndex));
  return result;
};

/** Escapes HTML special characters */
const escapeHtml = (str: string): string =>
  str.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#039;");

export const PROFANITY_ERROR_MESSAGE =
  "We can't accept the enquiry that use profanity, please remove or rewrite.";
